/**
 * Recently viewed products.
 *
 * Used by sections/product-recommendations.liquid when the section's Type is
 * "Recently viewed". The element records the current product (data-record-id),
 * then re-renders its own section through the Section Rendering API with a
 * search query for the stored product ids, so the markup is the same
 * product-card block tree the merchant configured in the editor.
 */
if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    static #STORAGE_KEY = 'theme:recently-viewed-products';
    static #MAX_STORED = 20;

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.isPreviewMode = this.dataset.isPreviewMode === 'true';
      this.excludeId = this.dataset.excludeId ? parseInt(this.dataset.excludeId, 10) : null;
      this.productsToShow = parseInt(this.dataset.productsToShow, 10) || 6;

      if (this.dataset.recordId) {
        RecentlyViewedProducts.record(parseInt(this.dataset.recordId, 10));
      }

      // The element is rendered hidden until the section has something to show.
      if (!this.dataset.hydrated) {
        this.renderTimeout = setTimeout(() => this.render(), 50);
      }

      if (window.Shopify?.designMode) {
        this.sectionLoadHandler = () => {
          clearTimeout(this.renderTimeout);
          this.renderTimeout = setTimeout(() => this.render(), 50);
        };
        document.addEventListener('shopify:section:load', this.sectionLoadHandler);
      }
    }

    /** @param {number} productId */
    static record(productId) {
      try {
        const key = RecentlyViewedProducts.#STORAGE_KEY;
        let viewed = JSON.parse(localStorage.getItem(key) || '[]');
        viewed = viewed.filter((id) => id !== productId);
        viewed.unshift(productId);
        localStorage.setItem(key, JSON.stringify(viewed.slice(0, RecentlyViewedProducts.#MAX_STORED)));
      } catch (e) {
        /* storage unavailable: nothing to record */
      }
    }

    static getProducts() {
      try {
        return JSON.parse(localStorage.getItem(RecentlyViewedProducts.#STORAGE_KEY) || '[]');
      } catch (e) {
        return [];
      }
    }

    async render() {
      const ids = RecentlyViewedProducts.getProducts().filter((id) => id !== this.excludeId);

      if (!ids.length) {
        if (!this.isPreviewMode) this.style.display = 'none';
        return;
      }

      const query = ids
        .slice(0, this.productsToShow)
        .map((id) => `id:${id}`)
        .join(' OR ');
      const url = `${window.Shopify.routes.root}search?type=product&q=${encodeURIComponent(query)}&section_id=${this.sectionId}`;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recently viewed fetch failed: ${response.status}`);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('recently-viewed-products');
        const hasCards = fresh?.querySelector('.resource-list__item');

        if (fresh && hasCards) {
          this.dataset.hydrated = 'true';
          this.replaceChildren(...fresh.childNodes);
          this.style.display = '';
          window.eventBus?.emit('recentlyViewed:updated');
        } else if (!this.isPreviewMode) {
          this.style.display = 'none';
        }
      } catch (e) {
        console.error(e);
        if (!this.isPreviewMode) this.style.display = 'none';
      }
    }

    disconnectedCallback() {
      clearTimeout(this.renderTimeout);
      if (this.sectionLoadHandler) {
        document.removeEventListener('shopify:section:load', this.sectionLoadHandler);
      }
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
